import {
  Dispatch,
  FC,
  FormEventHandler,
  RefObject,
  SetStateAction,
  SyntheticEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getBookingById } from 'services';
import { Booking } from 'types';

import { useResponse } from 'contexts/response';
import { useBookingApi } from 'hooks/use-booking-api';
import EditBookingForm from './edit-booking-form';

type EditBookingContextType = {
  didSubmit: boolean;
  booking?: Booking | null;
  bookingId: number;
  formRef: RefObject<HTMLFormElement>;
  handleReset: React.FormEventHandler<HTMLFormElement>;
  handleSubmit: React.FormEventHandler<HTMLFormElement>;
  setBooking: Dispatch<SetStateAction<Booking | null | undefined>>;
};

const EditBookingContext = createContext<EditBookingContextType>({
  didSubmit: false,
  bookingId: 0,
  formRef: { current: null },
  handleReset: () => {},
  handleSubmit: () => {},
  setBooking: () => {}
});

export const useEditBookings = () => useContext(EditBookingContext);

const EditBooking: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const bookingId = useMemo(() => Number(params.bookingId), [params.bookingId]);

  const { putBooking } = useBookingApi();
  const { clearError } = useResponse();

  const [booking, setBooking] = useState<Booking | null>();
  const [didSubmit, setDidSubmit] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (bookingId) getBookingById(bookingId).then(setBooking);
  }, [bookingId, setBooking]);

  const handleReset = useCallback(() => {
    clearError();
    if (!bookingId) navigate('/bookings');
    else navigate(`/bookings/${bookingId}`, { replace: true });
  }, [bookingId, clearError, navigate]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
      e.preventDefault();
      if (!booking) return;
      setDidSubmit(true);

      const response = await putBooking(booking);
      if (response) navigate(`/bookings/${booking.id}`);
      setDidSubmit(false);
    },
    [booking, navigate, putBooking]
  );

  return (
    <EditBookingContext.Provider
      value={{
        booking,
        bookingId,
        didSubmit,
        formRef,
        handleReset,
        handleSubmit,
        setBooking
      }}
    >
      <EditBookingForm />
    </EditBookingContext.Provider>
  );
};

export default EditBooking;
